import { clamp } from '../utils/math-helpers.js';

const HUD_SYMBOL_SLOTS = 10;
const HEALTH_SYMBOL = '\u2665';
const HUNGER_SYMBOL = '\ud83c\udf56';
const ARMOR_SYMBOL = '\u26e8';

function renderSymbolMeter(el, value, maxValue, symbol) {
  if (!el) return;
  const clamped = clamp(value, 0, maxValue);
  const totalUnits = HUD_SYMBOL_SLOTS * 2;
  const fillUnits = Math.round((clamped / maxValue) * totalUnits);

  let html = '';
  for (let i = 0; i < HUD_SYMBOL_SLOTS; i++) {
    const unit = fillUnits - (i * 2);
    const cls = unit >= 2 ? 'full' : unit === 1 ? 'half' : '';
    html += `<span class="symbol ${cls}">${symbol}</span>`;
  }
  el.innerHTML = html;
}

export function createSurvivalSystem({
  ui,
  itemDefs,
  sounds,
  t,
  isBlocking = null,
  onBlockDamage = null,
  onDamageFeedback = null,
  onConsumeBlocked = null,
  maxHealth = 20,
  maxHunger = 20,
  creativeMode = false,
  onPlayerDeath = null
}) {
  let creativeModeEnabled = !!creativeMode;
  let playerHealth = maxHealth;
  let playerHunger = maxHunger;
  let damageCooldown = 0;
  let starvationTick = 0;
  let regenTick = 0;
  const maxAirSeconds = 15;
  let airSeconds = maxAirSeconds;
  let drowningTick = 0;
  let lavaTick = 0;
  let onFireDuration = 0;
  let fireDamageTick = 0;
  let burnHazardLatched = false;
  const HUD_FULL_EPSILON = 0.49;

  const healthLabelEl = document.getElementById('health-label');
  const healthIconsEl = document.getElementById('health-icons');
  const hungerLabelEl = document.getElementById('hunger-label');
  const hungerIconsEl = document.getElementById('hunger-icons');
  const armorLabelEl = document.getElementById('armor-label');
  const armorIconsEl = document.getElementById('armor-icons');

  function updateHealthUI() {
    if (healthLabelEl) {
      healthLabelEl.innerText = t('hud.health_value', {
        value: Math.round(playerHealth),
        max: maxHealth
      });
    }
    renderSymbolMeter(healthIconsEl, playerHealth, maxHealth, HEALTH_SYMBOL);
  }

  function updateHungerUI() {
    if (hungerLabelEl) {
      hungerLabelEl.innerText = t('hud.hunger_value', {
        value: Math.round(playerHunger),
        max: maxHunger
      });
    }
    renderSymbolMeter(hungerIconsEl, playerHunger, maxHunger, HUNGER_SYMBOL);
  }

  function updateArmorUI() {
    const armorPoints = Math.max(0, Math.min(20, Number(ui.getArmorPoints?.() || 0)));
    if (armorLabelEl) {
      armorLabelEl.innerText = t('hud.armor_value', {
        value: Math.round(armorPoints),
        max: 20
      });
    }
    renderSymbolMeter(armorIconsEl, armorPoints, 20, ARMOR_SYMBOL);
  }

  function changePlayerHunger(deltaValue) {
    playerHunger = clamp(playerHunger + deltaValue, 0, maxHunger);
    updateHungerUI();
  }

  function applyDamage(amount, type = 'mob', meta = null) {
    if (creativeModeEnabled) return false;

    const bypassCooldown = type === 'void';
    if ((!bypassCooldown && damageCooldown > 0) || playerHealth <= 0) return false;

    let sourcePosition = null;
    if (meta && meta.sourcePosition) {
      const sp = meta.sourcePosition;
      if (Number.isFinite(sp.x) && Number.isFinite(sp.y) && Number.isFinite(sp.z)) {
        sourcePosition = { x: sp.x, y: sp.y, z: sp.z };
      }
    }

    const canBlockType = (
      type === 'mob' ||
      type === 'zombie' ||
      type === 'skeleton' ||
      type === 'skeleton_archer' ||
      type === 'husk' ||
      type === 'stray' ||
      type === 'drowned' ||
      type === 'pillager' ||
      type === 'raider_brute' ||
      type === 'spider' ||
      type === 'enderman' ||
      type === 'creeper_explode' ||
      type === 'ender_dragon'
    );
    const blocking = canBlockType && typeof isBlocking === 'function' && !!isBlocking();
    if (blocking && typeof onBlockDamage === 'function') onBlockDamage(type);

    const armorReduction = Math.max(0, Math.min(0.8, Number(ui.getArmorReduction?.() || 0)));
    const blockedFactor = blocking ? (type === 'creeper_explode' ? 0.65 : 0.4) : 1.0;
    const finalDamage = bypassCooldown
      ? maxHealth
      : Math.max(0.35, amount * blockedFactor * (1 - armorReduction));
    playerHealth = Math.max(0, playerHealth - finalDamage);
    if (type !== 'starve' && ui.damageEquippedArmor) ui.damageEquippedArmor(1);
    damageCooldown = bypassCooldown ? 0 : (type === 'fall' ? 0.3 : 0.45);
    if (sounds?.hurt) sounds.hurt(type);
    updateHealthUI();
    if (typeof onDamageFeedback === 'function') {
      onDamageFeedback({
        type,
        amount: finalDamage,
        blocked: blocking,
        sourcePosition
      });
    }
    if (playerHealth <= 0 && onPlayerDeath) {
      onPlayerDeath({
        type,
        sourcePosition
      });
    }
    return true;
  }

  function tryConsumeActiveFood() {
    if (creativeModeEnabled) return false;

    const activeId = ui.getActiveItemId();
    if (!activeId || ui.getActiveItemCount() <= 0) return false;

    const def = itemDefs[activeId];
    if (!def || def.kind !== 'food') return false;

    const nutrition = Math.max(0, Number(def.nutrition) || 0);
    const heal = Math.max(0, Number(def.heal) || 0);
    const isMedicine = !!def.isMedicine || (nutrition <= 0 && heal > 0);
    const healthIsFull = playerHealth >= (maxHealth - HUD_FULL_EPSILON);
    const hungerIsFull = playerHunger >= (maxHunger - HUD_FULL_EPSILON);

    if (isMedicine) {
      if (healthIsFull) {
        if (typeof onConsumeBlocked === 'function') onConsumeBlocked('health_full', def);
        return false;
      }
    } else if (hungerIsFull) {
      if (typeof onConsumeBlocked === 'function') onConsumeBlocked('hunger_full', def);
      return false;
    }
    if (nutrition <= 0 && heal <= 0) return false;

    if (!ui.consumeActiveItem(1)) return false;

    if (nutrition > 0) changePlayerHunger(nutrition);
    if (heal > 0 && playerHealth < maxHealth) {
      playerHealth = Math.min(maxHealth, playerHealth + heal);
      updateHealthUI();
    }

    if (sounds?.eat) sounds.eat();
    return true;
  }

  function update(delta, movedDistance, isMining, headInWater = false, inLava = false, headInLava = false, inFire = false, inWater = false) {
    if (creativeModeEnabled) {
      damageCooldown = 0;
      starvationTick = 0;
      regenTick = 0;
      airSeconds = maxAirSeconds;
      drowningTick = 0;
      lavaTick = 0;
      onFireDuration = 0;
      fireDamageTick = 0;
      burnHazardLatched = false;

      if (playerHealth !== maxHealth) {
        playerHealth = maxHealth;
        updateHealthUI();
      }
      if (playerHunger !== maxHunger) {
        playerHunger = maxHunger;
        updateHungerUI();
      }
      updateArmorUI();
      return;
    }

    damageCooldown = Math.max(0, damageCooldown - delta);

    const baseDrain = delta * 0.003;
    const moveDrain = movedDistance * 0.008;
    const miningDrain = isMining ? delta * 0.02 : 0;
    changePlayerHunger(-(baseDrain + moveDrain + miningDrain));

    if (playerHunger <= 0.01) {
      starvationTick += delta;
      if (starvationTick >= 4.0) {
        starvationTick = 0;
        applyDamage(1, 'starve');
      }
    } else {
      starvationTick = Math.max(0, starvationTick - delta * 0.5);
    }

    const canRegen = playerHunger >= 16 && playerHealth < maxHealth && damageCooldown <= 0;
    if (canRegen) {
      regenTick += delta;
      if (regenTick >= 2.5) {
        regenTick = 0;
        playerHealth = Math.min(maxHealth, playerHealth + 1);
        updateHealthUI();
        changePlayerHunger(-0.5);
      }
    } else {
      regenTick = 0;
    }

    if (headInWater) {
      airSeconds = Math.max(0, airSeconds - delta);
      if (airSeconds <= 0) {
        drowningTick += delta;
        if (drowningTick >= 1.0) {
          drowningTick = 0;
          applyDamage(2, 'drown');
        }
      }
    } else {
      airSeconds = Math.min(maxAirSeconds, airSeconds + delta * 2.6);
      if (airSeconds > 1.0) drowningTick = 0;
    }

    const burnHazardActive = inLava || headInLava || inFire;
    if (burnHazardActive && !burnHazardLatched) {
      sounds?.sizzle?.();
    }
    burnHazardLatched = burnHazardActive;

    if (inLava || headInLava) {
      const tickInterval = headInLava ? 0.55 : 0.72;
      const lavaDamage = headInLava ? 4 : 3;
      lavaTick += delta;
      while (lavaTick >= tickInterval) {
        lavaTick -= tickInterval;
        applyDamage(lavaDamage, 'lava');
      }
    } else {
      lavaTick = Math.max(0, lavaTick - delta * 2.0);
    }

    if (inLava || headInLava || inFire) {
      onFireDuration = 8.0;
    } else if (headInWater || inWater) {
      onFireDuration = 0;
    }

    if (onFireDuration > 0) {
      onFireDuration -= delta;
      fireDamageTick += delta;
      if (fireDamageTick >= 1.0) {
        fireDamageTick -= 1.0;
        applyDamage(1, 'fire');
      }
    } else {
      fireDamageTick = 0;
    }

    updateArmorUI();
  }

  function reset() {
    playerHealth = maxHealth;
    playerHunger = maxHunger;
    damageCooldown = 0;
    starvationTick = 0;
    regenTick = 0;
    airSeconds = maxAirSeconds;
    drowningTick = 0;
    lavaTick = 0;
    onFireDuration = 0;
    fireDamageTick = 0;
    burnHazardLatched = false;
    updateHealthUI();
    updateHungerUI();
    updateArmorUI();
  }

  function refreshLocale() {
    updateHealthUI();
    updateHungerUI();
    updateArmorUI();
  }

  function setCreativeMode(enabled) {
    const next = !!enabled;
    if (creativeModeEnabled === next) return;
    creativeModeEnabled = next;
    if (creativeModeEnabled) {
      playerHealth = maxHealth;
      playerHunger = maxHunger;
      damageCooldown = 0;
      starvationTick = 0;
      regenTick = 0;
      airSeconds = maxAirSeconds;
      drowningTick = 0;
      lavaTick = 0;
      onFireDuration = 0;
      fireDamageTick = 0;
      burnHazardLatched = false;
    }
    refreshLocale();
  }

  refreshLocale();

  return {
    applyDamage,
    tryConsumeActiveFood,
    update,
    reset,
    refreshLocale,
    exportState() {
      return {
        health: playerHealth,
        hunger: playerHunger,
        damageCooldown,
        airSeconds
      };
    },
    importState(snapshot = null) {
      const nextHealth = Number(snapshot?.health);
      const nextHunger = Number(snapshot?.hunger);
      const nextDamageCooldown = Number(snapshot?.damageCooldown);
      const nextAirSeconds = Number(snapshot?.airSeconds);

      if (creativeModeEnabled) {
        playerHealth = maxHealth;
        playerHunger = maxHunger;
        damageCooldown = 0;
        airSeconds = maxAirSeconds;
        starvationTick = 0;
        regenTick = 0;
        drowningTick = 0;
        lavaTick = 0;
        onFireDuration = 0;
        fireDamageTick = 0;
        burnHazardLatched = false;
      } else {
        if (Number.isFinite(nextHealth)) playerHealth = clamp(nextHealth, 0, maxHealth);
        if (Number.isFinite(nextHunger)) playerHunger = clamp(nextHunger, 0, maxHunger);
        if (Number.isFinite(nextDamageCooldown)) damageCooldown = Math.max(0, nextDamageCooldown);
        if (Number.isFinite(nextAirSeconds)) airSeconds = clamp(nextAirSeconds, 0, maxAirSeconds);
        starvationTick = 0;
        regenTick = 0;
        drowningTick = 0;
        lavaTick = 0;
        onFireDuration = 0;
        fireDamageTick = 0;
        burnHazardLatched = false;
      }

      updateHealthUI();
      updateHungerUI();
      updateArmorUI();
    },
    setCreativeMode,
    isCreativeMode() {
      return creativeModeEnabled;
    },
    getHealth() {
      return playerHealth;
    },
    getHunger() {
      return playerHunger;
    },
    getDamageCooldown() {
      return damageCooldown;
    },
    getOnFireDuration() {
      return onFireDuration;
    }
  };
}
